import { createContext } from 'preact';
import { ReactNode } from 'preact/compat';

export interface ChatConfiguration {
  canShowWelcomeMessage?: boolean;
  allowsMembersOnlyMode?: boolean;
  showPinButton?: boolean;
  showMediaUploadButton?: boolean;

  backgroundColor: string;
  foregroundColor: string;

  scrollbarBackgroundColor: string;
  scrollbarBorderColor: string;

  selectedBackgroundColor: string;
  pinnedBackgroundColor: string;

  /**
   * The timestamp in ms after which messages should be displayed.
   * Pinned messages are always shown.
   */
  showMessagesSinceTimestamp?: bigint;
  hideDefaultInputBox?: boolean;
  alignMessagesToBottom?: boolean;
  renderExtraMessage?: () => ReactNode;

  /**
   * When true, the extra message slides in only when user scrolls to the bottom.
   * Slides out when user scrolls away from bottom.
   */
  showExtraMessageOnlyAtBottom?: boolean;

  /**
   * When true, media thumbnails in chat will be blurred with a lock icon overlay.
   * Used for partner donation pages to encourage donations before viewing UGC.
   */
  lockMediaThumbnails?: boolean;

  /**
   * ---
   * When 'top', chat will scroll to the oldest message after ALL messages are loaded.
   * Useful for showing timeline from the beginning (e.g., after donation unlock).
   *
   * This option also triggers auto-fetching of all messages due to PubNub limitations:
   * 1. PubNub limits responses to 25 messages when using `includeMessageActions: true`
   *    (needed for reactions, pins, deletions), regardless of the `count` parameter.
   * 2. PubNub always fetches newest messages first - there's no way to fetch oldest-first.
   *
   * Because of these constraints, to show the chat starting from the oldest message,
   * we must auto-paginate through all batches until all messages are loaded, then scroll to top.
   *
   * ---
   *
   * When 'bottom', chat will scroll to the latest message after initial render.
   * Useful for iframe/embed layouts where container height settles after messages load.(Usually not needed for standard components)
   *
   * ---
   * @default 'bottom'
   */
  scrollPositionOnMount?: 'top' | 'bottom';

  /**
   * When true, clicking a media thumbnail opens a new window to `/u/{username}?id={mediaId}`
   * instead of the default in-app media modal behavior.
   * Useful for partner donation iframe embeds.
   */
  openMediaInNewWindow?: boolean;
}

interface DerivedChatConfiguration extends ChatConfiguration {
  cssVariables: Record<string, string>;
}

export const legacyDesktopChatConfiguration: ChatConfiguration = {
  canShowWelcomeMessage: true,
  allowsMembersOnlyMode: true,
  showPinButton: true,
  backgroundColor: 'var(--primaryGreen)',
  foregroundColor: 'var(--white)',
  scrollbarBackgroundColor: 'var(--white)',
  scrollbarBorderColor: 'var(--primaryGreen)',
  selectedBackgroundColor: 'var(--hunterGreen)',
  pinnedBackgroundColor: 'var(--hunterGreen)',
};

export const legacyAlternateChatConfiguration: ChatConfiguration = {
  backgroundColor: 'var(--white)',
  foregroundColor: 'var(--charcoal)',
  scrollbarBackgroundColor: '#E7E7E7',
  scrollbarBorderColor: 'var(--white)',
  selectedBackgroundColor: '#F7F7F7',
  pinnedBackgroundColor: '#F0F0F0',
};

export const ChatConfigurationContext = createContext<DerivedChatConfiguration | undefined>(undefined);
